// frontend/admin-portal/src/components/treasury/ProfitBreakdownTabs.jsx
import React, { useState } from "react";
import { formatMoney } from "@fiberhome/shared-ui";

const TABS = [
  { id: "byCategory", label: "By Category" },
  { id: "byBrand", label: "By Brand" },
  { id: "byItem", label: "By Item" },
];

function Bar({ pct, tone = "default" }) {
  const color =
    tone === "danger"
      ? "#dc3545"
      : tone === "warning"
        ? "#f59e0b"
        : tone === "success"
          ? "#198754"
          : "#0d6efd";
  return (
    <div
      style={{
        height: 4,
        background: "#eef0f3",
        borderRadius: 999,
        overflow: "hidden",
        marginTop: 4,
      }}
    >
      <div
        style={{
          width: `${Math.max(0, Math.min(100, pct))}%`,
          height: "100%",
          background: color,
          transition: "width 0.3s ease",
        }}
      />
    </div>
  );
}

function Row({ label, sublabel, revenue, cost, profit, marginPct, currency }) {
  const tone =
    profit < 0
      ? "danger"
      : marginPct < 10
        ? "warning"
        : marginPct < 30
          ? "default"
          : "success";

  return (
    <div
      style={{
        padding: "0.55rem 0",
        borderBottom: "1px solid var(--fh-border, #eef0f3)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: "0.5rem",
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={label}
          >
            {label}
          </div>
          {sublabel && (
            <div
              style={{
                fontSize: 11,
                color: "var(--fh-text-muted, #6b7280)",
                marginTop: 1,
              }}
            >
              {sublabel}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: profit >= 0 ? "#198754" : "#dc3545",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatMoney(profit, { symbol: currency, decimals: 2 })}
          </div>
          <div
            style={{
              fontSize: 10.5,
              color: "var(--fh-text-muted, #6b7280)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {marginPct}% margin ·{" "}
            {formatMoney(revenue, { symbol: currency, decimals: 2 })}
          </div>
        </div>
      </div>
      <Bar pct={Math.max(0, marginPct)} tone={tone} />
    </div>
  );
}

export default function ProfitBreakdownTabs({
  breakdown,
  currency,
  emptyMessage = "No data for this view",
}) {
  const [tab, setTab] = useState("byCategory");

  if (!breakdown) {
    return <div className="fh-dashboard-empty">{emptyMessage}</div>;
  }

  const rows = breakdown[tab] || [];

  return (
    <div>
      {/* Tab switcher */}
      <div
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid var(--fh-border, #eef0f3)",
          marginBottom: "0.5rem",
        }}
      >
        {TABS.map((t) => {
          const count = (breakdown[t.id] || []).length;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                background: "transparent",
                border: "none",
                padding: "0.5rem 0.75rem",
                fontSize: 12,
                fontWeight: 600,
                color: active
                  ? "var(--fh-primary, #0d6efd)"
                  : "var(--fh-text-muted, #6b7280)",
                borderBottom: active
                  ? "2px solid var(--fh-primary, #0d6efd)"
                  : "2px solid transparent",
                cursor: "pointer",
                marginBottom: -1,
              }}
            >
              {t.label}
              {count > 0 && (
                <span
                  style={{
                    marginLeft: 4,
                    padding: "1px 6px",
                    fontSize: 10,
                    borderRadius: 999,
                    background: active
                      ? "rgba(13,110,253,0.15)"
                      : "rgba(107,114,128,0.12)",
                    color: active
                      ? "var(--fh-primary, #0d6efd)"
                      : "var(--fh-text-muted, #6b7280)",
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Rows */}
      {rows.length === 0 ? (
        <div className="fh-dashboard-empty">{emptyMessage}</div>
      ) : (
        <div style={{ maxHeight: 320, overflowY: "auto" }}>
          {tab === "byCategory" &&
            rows.map((r) => (
              <Row
                key={r.categoryId || "uncat"}
                label={r.categoryName}
                sublabel={`${r.productCount} product${
                  r.productCount === 1 ? "" : "s"
                }${r.categoryKind ? ` · ${r.categoryKind}` : ""}`}
                revenue={r.revenue}
                cost={r.cost}
                profit={r.profit}
                marginPct={r.marginPct}
                currency={currency}
              />
            ))}

          {tab === "byBrand" &&
            rows.map((r) => (
              <Row
                key={r.brandId || "nobrand"}
                label={r.brandName}
                sublabel={`${r.productCount} product${
                  r.productCount === 1 ? "" : "s"
                }`}
                revenue={r.revenue}
                cost={r.cost}
                profit={r.profit}
                marginPct={r.marginPct}
                currency={currency}
              />
            ))}

          {tab === "byItem" &&
            rows.map((r, i) => (
              <Row
                key={r.productId || `item-${i}`}
                label={r.name}
                sublabel={`${r.sku || "—"} · ${r.categoryName}${
                  r.brandName ? ` · ${r.brandName}` : ""
                }`}
                revenue={r.revenue}
                cost={r.cost}
                profit={r.profit}
                marginPct={r.marginPct}
                currency={currency}
              />
            ))}
        </div>
      )}
    </div>
  );
}
